import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, Loader2, Upload, X, ImageIcon, Film, Cuboid,
  Check, ShieldCheck, AlertTriangle, RefreshCw, Trash2, Pencil,
  Sparkles, ExternalLink,
} from "lucide-react";

import { api, API_BASE, APP_ORIGIN } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";


// Design tokens — mirrors the mockup so future pages picked up by the
// design agent stay pixel-consistent.
const T = {
  bg:        "#0f1117",
  elevated:  "#161923",
  card:      "#1a1e2a",
  border:    "#262b3a",
  borderHi:  "#343b4f",
  accent:    "#4a9eff",
  accentAlt: "#7c5cff",
  ok:        "#3ddc97",
  warn:      "#ffb84a",
  ink:       "#e6e9f0",
  muted:     "#a9b3c9",
  faint:     "#6c7180",
};


export default function NewsStudioAvatars() {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("mine"); // 'mine' | 'stock'
  const [creating, setCreating] = useState(false);
  const [config, setConfig] = useState(null);
  const [stockAvatars, setStockAvatars] = useState([]);
  const [renaming, setRenaming] = useState(null);   // {avatar_id, name}
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/news-studio/avatars");
      setAvatars((r.data?.items || []).filter((a) => a.status !== "deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load avatars");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get("/news-studio/avatars/config");
      setConfig(r.data);
    } catch { /* soft-fail — creation flow will refetch */ }
  }, []);

  const loadStock = useCallback(async () => {
    try {
      // Reuse the existing Avatar Studio stock catalog — no duplication.
      const r = await api.get("/avatar-studio/avatars?limit=48");
      setStockAvatars(r.data?.avatars || []);
    } catch {
      setStockAvatars([]);
    }
  }, []);

  useEffect(() => { load(); loadConfig(); }, [load, loadConfig]);
  useEffect(() => { if (tab === "stock" && stockAvatars.length === 0) loadStock(); }, [tab, stockAvatars.length, loadStock]);

  const onDelete = async (avatar_id) => {
    if (!await appConfirm("Remove this custom avatar? The consent audit log will be retained.")) return;
    try {
      await api.delete(`/news-studio/avatars/${avatar_id}`);
      toast.success("Avatar removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onRename = async () => {
    if (!renaming || !renaming.name.trim()) return;
    try {
      await api.patch(`/news-studio/avatars/${renaming.avatar_id}`, { name: renaming.name.trim() });
      toast.success("Renamed");
      setRenaming(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  return (
    <div
      data-testid="news-studio-avatars-page"
      className="mx-auto max-w-6xl px-6 py-8"
      style={{ color: T.ink, background: T.bg }}
    >
      {/* ── Breadcrumb ─────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-4 type-caption" style={{ color: T.muted }}>
        <Link to="/dashboard/news-studio"
              data-testid="news-avatars-breadcrumb-back"
              className="hover:text-white transition inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Video News Studio
        </Link>
        <span>/</span>
        <span style={{ color: T.ink }}>My Avatars</span>
      </div>

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="type-h1 text-white">My Avatars</div>
          <div className="type-body mt-1 max-w-2xl" style={{ color: T.muted }}>
            Manage stock and custom on-air avatars for Video News Studio scripts. Custom avatars
            use your own footage or photo through a connected provider.
          </div>
        </div>
        <button
          type="button"
          data-testid="news-avatars-new-btn"
          onClick={() => setCreating(true)}
          className="rounded-lg px-4 py-2 font-medium text-white transition inline-flex items-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${T.accent} 0%, ${T.accentAlt} 100%)`,
            borderRadius: 8,
          }}
        >
          <Plus size={16} /> New Custom Avatar
        </button>
      </div>

      {/* ── Tabs ───────────────────────────────────────────── */}
      <div className="mb-5 border-b flex items-center gap-6" style={{ borderColor: T.border }}>
        {[
          { id: "mine",  label: "My Avatars",   count: avatars.length },
          { id: "stock", label: "Stock Avatars", count: stockAvatars.length || "—" },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`news-avatars-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className="pb-3 -mb-px transition inline-flex items-center gap-2"
              style={{
                color: active ? "#ffffff" : T.muted,
                borderBottom: `2px solid ${active ? T.accent : "transparent"}`,
                fontSize: 14, fontWeight: 600,
              }}
            >
              {t.label}
              <span className="rounded-full px-1.5 py-0.5"
                    style={{ background: T.card, color: active ? T.accent : T.faint, fontSize: 10 }}>
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Avatar grid ────────────────────────────────────── */}
      {tab === "mine" ? (
        <MyAvatarsGrid
          avatars={avatars}
          loading={loading}
          onCreate={() => setCreating(true)}
          onDelete={onDelete}
          onRename={(a) => setRenaming({ avatar_id: a.avatar_id, name: a.name })}
        />
      ) : (
        <StockAvatarsGrid avatars={stockAvatars} />
      )}

      {/* ── Compliance strip ───────────────────────────────── */}
      <div
        className="mt-8 rounded-xl border p-4 flex items-start gap-3"
        style={{ background: T.elevated, borderColor: T.border, borderRadius: 14 }}
      >
        <span
          className="rounded-full px-2 py-0.5 font-mono uppercase tracking-wider inline-flex items-center gap-1 shrink-0"
          style={{ background: `${T.warn}22`, color: T.warn, fontSize: 10, fontWeight: 700 }}
        >
          <AlertTriangle size={10} /> Compliance
        </span>
        <div className="type-caption flex-1" style={{ color: T.muted }}>
          <b style={{ color: T.ink }}>Avatars require explicit consent.</b>{" "}
          You must have the right to depict every person you upload as an avatar. Creating avatars of
          public figures, celebrities, politicians, or non-consenting individuals violates our terms
          and may be illegal in your jurisdiction. Every custom avatar creation is logged with a
          consent attestation for audit purposes.
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────── */}
      {creating && (
        <CreateAvatarWizard
          config={config}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
      {renaming && (
        <RenameModal
          value={renaming.name}
          onChange={(name) => setRenaming({ ...renaming, name })}
          onClose={() => setRenaming(null)}
          onSubmit={onRename}
        />
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// MyAvatarsGrid — cards + "Add custom avatar" trailing dashed tile
// ────────────────────────────────────────────────────────────────────
function MyAvatarsGrid({ avatars, loading, onCreate, onDelete, onRename }) {
  return (
    <div
      data-testid="news-avatars-grid"
      className="grid gap-3"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
    >
      {loading && avatars.length === 0 ? (
        <div className="col-span-full text-center py-10 type-caption" style={{ color: T.muted }}>
          <Loader2 className="mx-auto mb-2 animate-spin" size={20} />
          Loading your avatars…
        </div>
      ) : (
        <>
          {avatars.map((a) => (
            <AvatarCard key={a.avatar_id} avatar={a} onDelete={onDelete} onRename={onRename} />
          ))}
          <button
            type="button"
            data-testid="news-avatars-add-tile"
            onClick={onCreate}
            className="rounded-xl border border-dashed p-6 min-h-[220px] flex flex-col items-center justify-center text-center transition hover:-translate-y-[1px]"
            style={{ background: "transparent", borderColor: T.borderHi, borderRadius: 10, color: T.muted }}
          >
            <Plus size={22} style={{ color: T.accent }} />
            <div className="mt-2" style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
              Add custom avatar
            </div>
            <div className="type-caption mt-1" style={{ color: T.faint }}>
              Photo, video, or provider-trained
            </div>
          </button>
        </>
      )}
    </div>
  );
}


function StockAvatarsGrid({ avatars }) {
  if (avatars.length === 0) {
    return (
      <div className="rounded-xl border p-6 text-center type-caption"
           style={{ background: T.card, borderColor: T.border, color: T.muted, borderRadius: 10 }}>
        <Loader2 className="mx-auto mb-2 animate-spin" size={18} />
        Loading stock avatar library…
      </div>
    );
  }
  return (
    <div
      data-testid="news-avatars-stock-grid"
      className="grid gap-3"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
    >
      {avatars.slice(0, 48).map((a) => (
        <div key={a.avatar_id || a.id}
             className="rounded-xl border overflow-hidden"
             style={{ background: T.card, borderColor: T.border, borderRadius: 10 }}>
          {a.preview_image_url ? (
            <img src={a.preview_image_url} alt="" className="w-full aspect-[3/4] object-cover" />
          ) : (
            <div className="w-full aspect-[3/4] flex items-center justify-center"
                 style={{ background: T.elevated }}>
              <ImageIcon size={22} style={{ color: T.faint }} />
            </div>
          )}
          <div className="p-3">
            <div className="text-white truncate" style={{ fontSize: 13, fontWeight: 600 }}>
              {a.name || a.avatar_name || "Stock"}
            </div>
            <div className="type-caption truncate mt-0.5" style={{ color: T.muted }}>
              {a.gender || "Talent"} · Synthesia stock
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// AvatarCard — with status pill + row actions
// ────────────────────────────────────────────────────────────────────
function AvatarCard({ avatar, onDelete, onRename }) {
  const statusMeta = STATUS_META[avatar.status] || STATUS_META.processing;
  const thumb = avatar.thumbnail_url
    ? (avatar.thumbnail_url.startsWith("http")
        ? avatar.thumbnail_url
        : `${avatar.thumbnail_url.startsWith("/api") ? APP_ORIGIN : API_BASE}${avatar.thumbnail_url}`)
    : "";
  return (
    <div
      data-testid={`news-avatar-card-${avatar.avatar_id}`}
      className="rounded-xl border overflow-hidden group relative"
      style={{ background: T.card, borderColor: T.border, borderRadius: 10 }}
    >
      <div
        className="w-full aspect-[3/4] relative overflow-hidden"
        style={{ background: T.elevated }}
      >
        {thumb ? (
          avatar.sub_method === "video_loop" ? (
            <video src={thumb} muted loop playsInline
                   className="w-full h-full object-cover"
                   onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                   onMouseLeave={(e) => e.currentTarget.pause()} />
          ) : (
            <img src={thumb} alt="" className="w-full h-full object-cover" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {avatar.status === "processing"
              ? <Loader2 size={26} className="animate-spin" style={{ color: T.accent }} />
              : <ImageIcon size={26} style={{ color: T.faint }} />}
          </div>
        )}
        {/* Status pill */}
        <span
          className="absolute top-2 left-2 rounded-full px-2 py-0.5 font-mono uppercase tracking-wider"
          style={{
            background: statusMeta.bg, color: statusMeta.color,
            fontSize: 10, fontWeight: 700,
          }}
        >
          {statusMeta.label}
        </span>
        {/* Row actions overlay */}
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
          <button type="button"
                  data-testid={`news-avatar-rename-${avatar.avatar_id}`}
                  onClick={() => onRename(avatar)}
                  className="rounded p-1"
                  style={{ background: "rgba(15,17,23,0.8)", color: T.muted }}
                  title="Rename">
            <Pencil size={12} />
          </button>
          <button type="button"
                  data-testid={`news-avatar-delete-${avatar.avatar_id}`}
                  onClick={() => onDelete(avatar.avatar_id)}
                  className="rounded p-1"
                  style={{ background: "rgba(15,17,23,0.8)", color: T.warn }}
                  title="Delete">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <div className="p-3">
        <div className="text-white truncate" style={{ fontSize: 13, fontWeight: 600 }}>
          {avatar.name}
        </div>
        <div className="type-caption mt-0.5 truncate" style={{ color: T.muted }}>
          {avatar.metadata_summary || avatar.source_provider}
        </div>
        <div className="type-caption mt-1 flex items-center gap-2" style={{ color: T.faint }}>
          <span className="rounded px-1.5 py-0.5" style={{ background: T.elevated, fontSize: 10 }}>
            {avatar.source_provider}
          </span>
          {avatar.status_detail && avatar.status !== "ready" && avatar.status !== "photo_ready" && (
            <span className="truncate" style={{ fontSize: 10 }}>{avatar.status_detail}</span>
          )}
        </div>
      </div>
    </div>
  );
}


const STATUS_META = {
  awaiting_upload:  { label: "Awaiting upload",  bg: "#26313f", color: T.muted   },
  processing:       { label: "Processing…",       bg: `${T.accent}22`, color: T.accent },
  photo_ready:      { label: "Ready",             bg: `${T.ok}22`, color: T.ok    },
  ready:            { label: "Ready",             bg: `${T.ok}22`, color: T.ok    },
  failed:           { label: "Failed",            bg: `${T.warn}22`, color: T.warn },
  deleted:          { label: "Deleted",           bg: "#26313f", color: T.faint  },
};


// ────────────────────────────────────────────────────────────────────
// Rename modal — small, focused
// ────────────────────────────────────────────────────────────────────
function RenameModal({ value, onChange, onClose, onSubmit }) {
  return (
    <ModalShell title="Rename avatar" onClose={onClose} testId="news-avatar-rename-modal">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, 80))}
        autoFocus
        placeholder="Avatar name"
        className="w-full rounded-lg px-3 py-2 outline-none"
        style={{ background: T.card, border: `1px solid ${T.border}`, color: T.ink, fontSize: 14, borderRadius: 8 }}
        onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
        data-testid="news-avatar-rename-input"
      />
      <ModalFooter onCancel={onClose} onSubmit={onSubmit} submitLabel="Save" submitTestId="news-avatar-rename-save" />
    </ModalShell>
  );
}


// ────────────────────────────────────────────────────────────────────
// Create wizard — 3-step flow (method → file → done)
// ────────────────────────────────────────────────────────────────────
function CreateAvatarWizard({ config, onClose, onCreated }) {
  const [step, setStep] = useState(1);         // 1: method  2: upload  3: done
  const [method, setMethod] = useState("direct_upload");
  const [subMethod, setSubMethod] = useState("still_photo");
  const [providerHint, setProviderHint] = useState("none");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [avatarId, setAvatarId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const canProceedStep1 = useMemo(() => {
    if (!name.trim() || !consent) return false;
    if (method === "direct_upload" && subMethod === "3d_model") return false;
    return true;
  }, [name, consent, method, subMethod]);

  const canSubmit = useMemo(() => {
    return !!file && !submitting;
  }, [file, submitting]);

  const createRow = async () => {
    setSubmitting(true);
    try {
      // Provider training paths are gated on the backend — surface a
      // clean redirect if the user tries.
      const r = await api.post("/news-studio/avatars", {
        name: name.trim(),
        method,
        sub_method: subMethod,
        provider_hint: providerHint,
        consent_attested: consent,
      });
      setAvatarId(r.data.avatar.avatar_id);
      setStep(2);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "string" && (d.includes("Training") || d.includes("training"))) {
        toast.info(d, {
          action: {
            label: "Connect provider",
            onClick: () => { window.location.href = "/dashboard/providers"; },
          },
        });
      } else {
        toast.error(d || "Failed to create avatar");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const uploadFile = async () => {
    if (!file || !avatarId) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/news-studio/avatars/${avatarId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Avatar ready — you can pick it inside a News Studio script");
      onCreated?.(r.data.avatar);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="New Custom Avatar"
      subtitle={
        <StepIndicator step={step} labels={["Choose method", "Upload file", "Ready to use"]} />
      }
      onClose={onClose}
      wide
      testId="news-avatars-create-modal"
    >
      {step === 1 && (
        <div className="space-y-5">
          <div>
            <label className="type-caption uppercase tracking-wider mb-1 block" style={{ color: T.muted }}>
              Avatar name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 80))}
              placeholder='e.g. "Anchor Sarah — evening bulletin"'
              className="w-full rounded-lg px-3 py-2 outline-none"
              style={{ background: T.card, border: `1px solid ${T.border}`, color: T.ink, fontSize: 14, borderRadius: 8 }}
              data-testid="news-avatars-name-input"
            />
          </div>

          <div>
            <label className="type-caption uppercase tracking-wider mb-2 block" style={{ color: T.muted }}>
              Method
            </label>
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
              <MethodCard
                id="direct_upload"
                title="Direct Upload"
                blurb="Bring your own file. Fastest path — no provider training required."
                requirement="Recommended"
                requirementColor={T.ok}
                selected={method === "direct_upload"}
                onSelect={() => setMethod("direct_upload")}
                testId="method-direct_upload"
              />
              <MethodCard
                id="heygen_training"
                title="HeyGen"
                blurb="Train an avatar from your anchor's video footage — highest lip-sync fidelity."
                requirement="Requires HeyGen Business plan"
                requirementColor={T.warn}
                selected={method === "heygen_training"}
                onSelect={() => setMethod("heygen_training")}
                testId="method-heygen"
              />
              <MethodCard
                id="did_training"
                title="D-ID"
                blurb="Train from video or a photo directly on D-ID's Studio platform."
                requirement="Requires D-ID Pro plan"
                requirementColor={T.warn}
                selected={method === "did_training"}
                onSelect={() => setMethod("did_training")}
                testId="method-did"
              />
            </div>
          </div>

          {method === "direct_upload" && (
            <div>
              <label className="type-caption uppercase tracking-wider mb-2 block" style={{ color: T.muted }}>
                Upload type
              </label>
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <MethodCard
                  id="still_photo" icon={ImageIcon}
                  title="Still photo"
                  blurb="Portrait photo of the person. Instantly usable as a still + optionally animated by HeyGen/D-ID."
                  requirement="Recommended"
                  requirementColor={T.ok}
                  selected={subMethod === "still_photo"}
                  onSelect={() => setSubMethod("still_photo")}
                  testId="sub-still_photo"
                />
                <MethodCard
                  id="video_loop" icon={Film}
                  title="Pre-rendered video"
                  blurb="A 5-30 second video loop — perfect for cutaways or non-speaking bumpers."
                  requirement="No lip-sync"
                  requirementColor={T.muted}
                  selected={subMethod === "video_loop"}
                  onSelect={() => setSubMethod("video_loop")}
                  testId="sub-video_loop"
                />
                <MethodCard
                  id="3d_model" icon={Cuboid}
                  title="3D model"
                  blurb="Coming soon — glTF / FBX rigged model support."
                  requirement="Coming soon"
                  requirementColor={T.faint}
                  selected={false}
                  disabled
                  testId="sub-3d_model"
                />
              </div>
            </div>
          )}

          {method === "direct_upload" && subMethod === "still_photo" && (
            <div>
              <label className="type-caption uppercase tracking-wider mb-2 block" style={{ color: T.muted }}>
                Photo animation (optional)
              </label>
              <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                {[
                  { id: "none", label: "None (static thumbnail)", blurb: "Photo stays a still. Use News Studio's default renderer." },
                  { id: "heygen", label: "HeyGen photo API", blurb: "Route the photo to HeyGen's photo-avatar animator." },
                  { id: "did", label: "D-ID photo API", blurb: "Route the photo to D-ID's photo-avatar animator." },
                ].map((p) => (
                  <MethodCard
                    key={p.id}
                    id={p.id}
                    title={p.label}
                    blurb={p.blurb}
                    selected={providerHint === p.id}
                    onSelect={() => setProviderHint(p.id)}
                    testId={`provider-${p.id}`}
                    dense
                  />
                ))}
              </div>
            </div>
          )}

          {/* Consent gate */}
          <label className="rounded-xl border p-4 flex items-start gap-3 cursor-pointer"
                 style={{ background: T.card, borderColor: consent ? T.accent : T.border, borderRadius: 10 }}
                 data-testid="news-avatars-consent-gate">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0"
              style={{ accentColor: T.accent }}
              data-testid="news-avatars-consent-checkbox"
            />
            <div className="type-caption flex-1" style={{ color: T.muted }}>
              <b style={{ color: T.ink }}>Consent attestation (required)</b>
              <div className="mt-1">
                {config?.consent_text
                  || "I confirm that I have obtained explicit consent from the person depicted and I own or have licensed rights to the uploaded media."}
              </div>
            </div>
            {consent && <Check size={16} style={{ color: T.ok }} />}
          </label>
        </div>
      )}

      {step === 2 && (
        <UploadStep
          config={config}
          method={method}
          subMethod={subMethod}
          providerHint={providerHint}
          file={file}
          onPickFile={setFile}
          fileInputRef={fileInputRef}
        />
      )}

      <ModalFooter
        onCancel={onClose}
        onSubmit={step === 1 ? createRow : uploadFile}
        submitLabel={
          step === 1
            ? (method === "direct_upload" ? "Continue → Upload" : "Continue")
            : (submitting ? "Uploading…" : (subMethod === "still_photo" ? "Upload & Add Avatar" : "Upload video loop"))
        }
        submitDisabled={step === 1 ? !canProceedStep1 || submitting : !canSubmit}
        submitTestId="news-avatars-submit"
        footNote={
          step === 1
            ? "One custom avatar slot per creation. Consent is logged to an immutable audit trail."
            : `Max ${(config?.[subMethod === "still_photo" ? "max_photo_bytes" : "max_video_bytes"] / (1024*1024)).toFixed(0) || "—"} MB.`
        }
      />
    </ModalShell>
  );
}


function MethodCard({ id, title, blurb, requirement, requirementColor, selected, onSelect, disabled, icon: Icon, testId, dense }) {
  return (
    <button
      type="button"
      data-testid={testId || `method-${id}`}
      onClick={disabled ? undefined : onSelect}
      disabled={disabled}
      className="text-left rounded-xl border p-3 transition"
      style={{
        background: selected ? `${T.accent}12` : T.card,
        borderColor: selected ? T.accent : T.border,
        borderRadius: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-white flex items-center gap-2" style={{ fontSize: dense ? 12 : 13, fontWeight: 700 }}>
          {Icon && <Icon size={dense ? 12 : 14} style={{ color: T.accent }} />}
          {title}
        </div>
        {selected && <Check size={14} style={{ color: T.accent }} />}
      </div>
      <div className="type-caption" style={{ color: T.muted, fontSize: dense ? 10 : 11, lineHeight: 1.5 }}>
        {blurb}
      </div>
      {requirement && (
        <div className="type-caption mt-2" style={{ color: requirementColor || T.muted, fontSize: 10, fontWeight: 600 }}>
          {requirement}
        </div>
      )}
    </button>
  );
}


function UploadStep({ config, subMethod, providerHint, file, onPickFile, fileInputRef }) {
  const req = subMethod === "still_photo"
    ? config?.photo_avatar_requirements
    : config?.video_loop_requirements;
  const accept = subMethod === "still_photo"
    ? (config?.allowed_photo_types || []).join(",")
    : (config?.allowed_video_types || []).join(",");

  return (
    <div className="space-y-4">
      <label
        htmlFor="news-avatar-file-input"
        className="block rounded-xl border border-dashed p-8 text-center cursor-pointer"
        style={{
          background: file ? `${T.accent}08` : T.card,
          borderColor: file ? T.accent : T.borderHi,
          borderRadius: 10,
        }}
        data-testid="news-avatars-dropzone"
      >
        <Upload className="mx-auto mb-2" size={26} style={{ color: T.accent }} />
        <div className="text-white mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
          {file
            ? `${file.name} · ${(file.size / (1024*1024)).toFixed(1)} MB`
            : (subMethod === "still_photo" ? "Drop or click to upload a photo" : "Drop or click to upload a video loop")}
        </div>
        <div className="type-caption" style={{ color: T.muted }}>
          {subMethod === "still_photo"
            ? (providerHint !== "none"
                ? `Photo will be routed to ${providerHint.toUpperCase()}'s photo-avatar API after upload.`
                : "Uploaded as a static photo — use News Studio's built-in renderer.")
            : "Uploaded as a raw video loop — no lip-sync applied."}
        </div>
        <input
          id="news-avatar-file-input"
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="hidden"
          data-testid="news-avatars-file-input"
          onChange={(e) => onPickFile(e.target.files?.[0] || null)}
        />
      </label>

      {req && (
        <div className="rounded-xl border p-4"
             style={{ background: T.elevated, borderColor: T.border, borderRadius: 10 }}>
          <div className="text-white mb-2" style={{ fontSize: 12, fontWeight: 700 }}>
            Requirements
          </div>
          <ul className="space-y-1 type-caption" style={{ color: T.muted, fontSize: 11 }}>
            {Object.entries(req).map(([k, v]) => (
              <li key={k} className="flex items-start gap-2">
                <ShieldCheck size={10} className="mt-0.5 shrink-0" style={{ color: T.ok }} />
                <span><b style={{ color: T.ink, textTransform: "capitalize" }}>{k.replace(/_/g, " ")}:</b> {v}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Shared modal shell (used by both wizard + rename)
// ────────────────────────────────────────────────────────────────────
function ModalShell({ title, subtitle, onClose, children, wide, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-h-[90vh] overflow-y-auto rounded-xl border shadow-2xl"
        style={{
          background: T.elevated,
          borderColor: T.border,
          borderRadius: 14,
          maxWidth: wide ? 720 : 480,
        }}
      >
        <div
          className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b px-6 py-4"
          style={{ background: T.elevated, borderColor: T.border }}
        >
          <div className="flex-1">
            <div className="text-white" style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
            {subtitle && <div className="mt-2">{subtitle}</div>}
          </div>
          <button type="button"
                  onClick={onClose}
                  className="rounded p-1"
                  style={{ background: T.card, border: `1px solid ${T.border}`, color: T.muted }}
                  data-testid={`${testId}-close`}>
            <X size={14} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}


function ModalFooter({ onCancel, onSubmit, submitLabel, submitDisabled, submitTestId, footNote }) {
  return (
    <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
      <div className="type-caption" style={{ color: T.faint, fontSize: 10 }}>{footNote}</div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-3 py-1.5"
          style={{ background: T.card, border: `1px solid ${T.border}`, color: T.muted, borderRadius: 8, fontSize: 13 }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitDisabled}
          data-testid={submitTestId}
          className="rounded-lg px-4 py-1.5 font-medium text-white transition disabled:opacity-50"
          style={{
            background: `linear-gradient(135deg, ${T.accent} 0%, ${T.accentAlt} 100%)`,
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}


function StepIndicator({ step, labels }) {
  return (
    <div className="flex items-center gap-2 mt-1">
      {labels.map((label, i) => {
        const num = i + 1;
        const active = step === num;
        const done = step > num;
        return (
          <div key={label} className="flex items-center gap-2" data-testid={`news-avatars-step-${num}`}>
            <span
              className="rounded-full flex items-center justify-center font-mono font-bold"
              style={{
                width: 20, height: 20,
                background: done ? T.ok : (active ? T.accent : T.card),
                color: done || active ? "#0f1117" : T.muted,
                fontSize: 10,
              }}
            >
              {done ? <Check size={10} /> : num}
            </span>
            <span className="type-caption" style={{
              color: active ? "#ffffff" : T.muted,
              fontSize: 11,
              fontWeight: active ? 700 : 500,
            }}>
              {label}
            </span>
            {i < labels.length - 1 && (
              <span style={{ color: T.faint, marginLeft: 2, marginRight: 2 }}>→</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
